import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ApplicationQADialog from "@/components/dashboard/ApplicationQADialog";
import {
  FileText, Search, Clock, CheckCircle, AlertCircle, AlertTriangle, ChevronDown, ChevronUp,
  Plus, MapPin, User, Home, Users, PawPrint, Heart, Calendar, Shield, X, Tag
} from "lucide-react";

const STATUS_MAP = {
  // Sadeleştirilmiş 9 durumlu akış — Pativista optimize edilmiş süreçle uyumlu.
  // Detay (online/yüz yüze/ev ziyareti) not alanında tutulur; ayrı statülere gerek yok.
  yeni_basvuru: { label: "Yeni Başvuru", color: "bg-blue-100 text-blue-800", icon: Clock },
  on_degerlendirme: { label: "Ön Değerlendirme", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  gorusme_planlandi: { label: "Görüşme Planlandı", color: "bg-cyan-100 text-cyan-800", icon: Calendar },
  gorusme_tamamlandi: { label: "Görüşme Tamamlandı", color: "bg-teal-100 text-teal-800", icon: CheckCircle },
  sahiplenme_basladi: { label: "Sahiplenme Süreci (15 Gün)", color: "bg-emerald-100 text-emerald-800", icon: Heart },
  sahiplendirildi: { label: "Sahiplendirildi", color: "bg-green-200 text-green-900", icon: CheckCircle },
  reddedildi: { label: "Reddedildi", color: "bg-red-100 text-red-800", icon: X },
  vazgecti: { label: "Vazgeçti", color: "bg-gray-100 text-gray-800", icon: AlertCircle },
  iade_alindi: { label: "İade Alındı", color: "bg-rose-100 text-rose-800", icon: AlertTriangle }
};

import { RISK_LABEL_MAP } from "@/lib/riskLabels";

export default function ApplicationsPanel() {
  const [applications, setApplications] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedApp, setExpandedApp] = useState(null);
  const [qaApp, setQaApp] = useState(null);
  const [noteDialog, setNoteDialog] = useState({ open: false, appId: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, appId: null, current: "" });
  const [noteContent, setNoteContent] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");

  useEffect(() => { fetchApplications(); }, [statusFilter, riskFilter]);

  const fetchApplications = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (riskFilter !== "all") params.append("risk_label", riskFilter);
      const response = await axios.get(`${API}/pativista/applications?${params.toString()}`);
      setApplications(response.data.applications || []);
      setStatusCounts(response.data.status_counts || {});
    } catch (error) {
      toast.error("Başvurular yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus || !statusDialog.appId) return;
    try {
      await axios.put(`${API}/pativista/applications/${statusDialog.appId}/status`, {
        new_status: newStatus,
        note: statusNote || null
      });
      toast.success("Durum güncellendi");
      setStatusDialog({ open: false, appId: null, current: "" });
      setNewStatus("");
      setStatusNote("");
      fetchApplications();
    } catch (error) {
      toast.error("Durum güncellenemedi");
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !noteDialog.appId) return;
    try {
      await axios.post(`${API}/pativista/applications/${noteDialog.appId}/note`, {
        content: noteContent,
        note_type: noteType
      });
      toast.success("Not eklendi");
      setNoteDialog({ open: false, appId: null });
      setNoteContent("");
      setNoteType("general");
      fetchApplications();
    } catch (error) {
      toast.error("Not eklenemedi");
    }
  };

  const handleToggleRiskLabel = async (appId, label, action) => {
    try {
      await axios.post(`${API}/pativista/applications/${appId}/risk-label?label=${label}&action=${action}`);
      toast.success(action === "add" ? "Etiket eklendi" : "Etiket kaldırıldı");
      fetchApplications();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const filtered = applications.filter(app => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      app.ad_soyad?.toLowerCase().includes(q) ||
      app.dog_name?.toLowerCase().includes(q) ||
      app.sehir?.toLowerCase().includes(q) ||
      app.application_id?.toLowerCase().includes(q)
    );
  });

  const totalAll = Object.values(statusCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="applications-panel">
      <div>
        <h1 className="font-heading text-3xl">Başvuru Değerlendirme</h1>
        <p className="text-muted-foreground">{totalAll} toplam başvuru</p>
      </div>

      {/* Status summary bar */}
      <div className="flex flex-wrap gap-2" data-testid="status-summary">
        <Badge
          variant={statusFilter === "all" ? "default" : "outline"}
          className="cursor-pointer"
          onClick={() => setStatusFilter("all")}
          data-testid="filter-all"
        >
          Tümü ({totalAll})
        </Badge>
        {Object.entries(statusCounts).map(([status, count]) => {
          const info = STATUS_MAP[status];
          return (
            <Badge
              key={status}
              variant={statusFilter === status ? "default" : "outline"}
              className={`cursor-pointer ${statusFilter === status ? "" : info?.color || ""}`}
              onClick={() => setStatusFilter(status === statusFilter ? "all" : status)}
              data-testid={`filter-status-${status}`}
            >
              {info?.label || status} ({count})
            </Badge>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Ad, köpek, şehir veya başvuru no ile ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-applications"
          />
        </div>
        <Button
          variant="outline"
          onClick={async () => {
            try {
              const cfg = { withCredentials: true };
              const dry = await axios.post(`${API}/admin/applications/recompute-risk-labels`, { dry_run: true }, cfg);
              if (!dry.data.changed) return toast.info("Tüm başvuruların etiketleri güncel");
              if (!window.confirm(`${dry.data.changed} başvurunun uyarı etiketleri güncellenecek (elle eklediğiniz etiketler korunur). Devam edilsin mi?`)) return;
              const res = await axios.post(`${API}/admin/applications/recompute-risk-labels`, { dry_run: false }, cfg);
              toast.success(`${res.data.changed} başvuru güncellendi`);
              fetchApplications();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Yeniden hesaplama başarısız");
            }
          }}
          data-testid="recompute-risk-labels"
        >
          <AlertTriangle className="w-4 h-4 mr-1" /> Uyarıları Yeniden Tara
        </Button>
        <Select value={riskFilter} onValueChange={setRiskFilter}>
          <SelectTrigger className="w-[220px]" data-testid="risk-filter">
            <SelectValue placeholder="Risk Etiketi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Etiketler</SelectItem>
            {Object.entries(RISK_LABEL_MAP).map(([key, info]) => (
              <SelectItem key={key} value={key}>{info.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Applications list */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="spinner"></div></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-card border border-border/50">
          <CardContent className="py-20 text-center">
            <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">Başvuru bulunamadı</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="applications-list">
          {filtered.map((app) => (
            <ApplicationCard
              key={app.application_id}
              app={app}
              expanded={expandedApp === app.application_id}
              onToggle={() => setExpandedApp(expandedApp === app.application_id ? null : app.application_id)}
              onStatusChange={() => setStatusDialog({ open: true, appId: app.application_id, current: app.status })}
              onAddNote={() => setNoteDialog({ open: true, appId: app.application_id })}
              onOpenQA={() => setQaApp(app)}
              onToggleRisk={(label, action) => handleToggleRiskLabel(app.application_id, label, action)}
            />
          ))}
        </div>
      )}

      <ApplicationQADialog app={qaApp} open={!!qaApp} onOpenChange={(v) => !v && setQaApp(null)} />

      {/* Status Change Dialog */}
      <Dialog open={statusDialog.open} onOpenChange={(v) => !v && setStatusDialog({ open: false, appId: null, current: "" })}>
        <DialogContent data-testid="status-change-dialog">
          <DialogHeader>
            <DialogTitle>Durum Değiştir</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mevcut Durum</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {STATUS_MAP[statusDialog.current]?.label || statusDialog.current}
              </p>
            </div>
            <div>
              <Label>Yeni Durum</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="mt-1.5" data-testid="new-status-select">
                  <SelectValue placeholder="Durum seçin" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_MAP).map(([key, info]) => (
                    <SelectItem key={key} value={key}>{info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Not (opsiyonel)</Label>
              <Textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Durum değişikliği hakkında not..."
                className="mt-1.5"
                data-testid="status-note-input"
              />
            </div>
            <Button onClick={handleStatusChange} disabled={!newStatus} className="w-full" data-testid="status-change-submit">
              Durumu Güncelle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={(v) => !v && setNoteDialog({ open: false, appId: null })}>
        <DialogContent data-testid="add-note-dialog">
          <DialogHeader>
            <DialogTitle>Not Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Not Tipi</Label>
              <Select value={noteType} onValueChange={setNoteType}>
                <SelectTrigger className="mt-1.5" data-testid="note-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Genel</SelectItem>
                  <SelectItem value="risk">Risk</SelectItem>
                  <SelectItem value="positive">Olumlu</SelectItem>
                  <SelectItem value="follow_up">Takip</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Not</Label>
              <Textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Notunuzu yazın..."
                className="mt-1.5"
                rows={4}
                data-testid="note-content-input"
              />
            </div>
            <Button onClick={handleAddNote} disabled={!noteContent.trim()} className="w-full" data-testid="add-note-submit">
              Not Ekle
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApplicationCard({ app, expanded, onToggle, onStatusChange, onAddNote, onToggleRisk, onOpenQA }) {
  const statusInfo = STATUS_MAP[app.status] || { label: app.status, color: "bg-gray-100 text-gray-800" };
  const StatusIcon = statusInfo.icon || Clock;
  const daysSince = Math.floor((Date.now() - new Date(app.created_at).getTime()) / 86400000);

  let chipCountdown = null;
  if (app.status === "sahiplenme_basladi" && app.sahiplenme_baslangic) {
    const start = new Date(app.sahiplenme_baslangic);
    const elapsed = Math.floor((Date.now() - start.getTime()) / 86400000);
    chipCountdown = Math.max(0, 15 - elapsed);
  }

  return (
    <Card className="bg-card border border-border/50 overflow-hidden" data-testid={`application-card-${app.application_id}`}>
      <CardContent className="p-0">
        <div
          className="p-4 cursor-pointer hover:bg-muted/20 transition-colors flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-3 md:gap-4"
          onClick={onToggle}
          data-testid={`application-toggle-${app.application_id}`}
        >
          <div className="flex items-start gap-3 w-full min-w-0 md:w-auto md:flex-1 md:items-center order-1">
            <StatusIcon className="w-5 h-5 shrink-0 mt-0.5 md:mt-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium break-words">{app.ad_soyad}</span>
                <span className="text-muted-foreground text-sm">&rarr;</span>
                <span className="font-medium text-primary break-words">{app.dog_name}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 flex-wrap">
                <MapPin className="w-3 h-3 shrink-0" />
                <span>{app.sehir}, {app.ilce}</span>
                <span className="hidden sm:inline">|</span>
                <span>{daysSince} gün önce</span>
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-8 order-2 md:order-3 shrink-0"
            onClick={(e) => { e.stopPropagation(); onOpenQA(); }}
            data-testid={`btn-qa-${app.application_id}`}
          >
            <FileText className="w-4 h-4 mr-1" /> Soru-Cevap
          </Button>
          <div className="flex items-center gap-2 shrink-0 ml-auto md:ml-0 order-3 md:order-5">
            <Badge className={`${statusInfo.color} text-xs`} data-testid={`app-status-badge-${app.application_id}`}>
              {statusInfo.label}
            </Badge>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
          <div className="hidden md:flex flex-wrap gap-1 order-4">
            {(app.risk_labels || []).slice(0, 3).map(label => {
              const info = RISK_LABEL_MAP[label];
              return (
                <span key={label} className={`text-xs px-2 py-0.5 rounded-full ${info?.color || "bg-gray-100 text-gray-600"}`}>
                  {info?.label || label}
                </span>
              );
            })}
            {(app.risk_labels || []).length > 3 && (
              <span className="text-xs text-muted-foreground">+{app.risk_labels.length - 3}</span>
            )}
          </div>
        </div>

        {expanded && (
          <div className="border-t border-border/50 p-4 space-y-6" data-testid={`application-detail-${app.application_id}`}>
            {chipCountdown !== null && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
                <Shield className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">15 Gün Deneme Süresi</p>
                  <p className="text-xs text-emerald-600">
                    {chipCountdown > 0
                      ? `Çip devrine ${chipCountdown} gün kaldı`
                      : "15 gün tamamlandı - Çip devrini başlatın!"}
                  </p>
                </div>
                {chipCountdown === 0 && (
                  <Button size="sm" className="ml-auto" onClick={onStatusChange} data-testid="chip-transfer-btn">
                    Çip Devrini Başlat
                  </Button>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2" data-testid="app-actions">
              <Button size="sm" onClick={onOpenQA} data-testid={`btn-qa-detail-${app.application_id}`}>
                <FileText className="w-4 h-4 mr-1" /> Tüm Soru-Cevapları Gör
              </Button>
              <Button size="sm" variant="outline" onClick={onStatusChange} data-testid={`btn-status-change-${app.application_id}`}>
                <Clock className="w-4 h-4 mr-1" /> Durum Değiştir
              </Button>
              <Button size="sm" variant="outline" onClick={onAddNote} data-testid={`btn-add-note-${app.application_id}`}>
                <Plus className="w-4 h-4 mr-1" /> Not Ekle
              </Button>
              {!(app.risk_labels || []).includes("ev_ziyareti_oner") ? (
                <Button
                  size="sm" variant="outline"
                  className="text-orange-600 border-orange-300"
                  onClick={() => onToggleRisk("ev_ziyareti_oner", "add")}
                  data-testid={`btn-ev-ziyareti-${app.application_id}`}
                >
                  <Home className="w-4 h-4 mr-1" /> Ev Ziyareti Gerekli
                </Button>
              ) : (
                <Button
                  size="sm" variant="outline"
                  className="text-muted-foreground"
                  onClick={() => onToggleRisk("ev_ziyareti_oner", "remove")}
                >
                  <X className="w-4 h-4 mr-1" /> Ev Ziyareti Kaldır
                </Button>
              )}
              {!(app.risk_labels || []).includes("supheli_durum") ? (
                <Button
                  size="sm" variant="outline"
                  className="text-red-600 border-red-300"
                  onClick={() => onToggleRisk("supheli_durum", "add")}
                  data-testid={`btn-supheli-${app.application_id}`}
                >
                  <AlertTriangle className="w-4 h-4 mr-1" /> Şüpheli Durum
                </Button>
              ) : (
                <Button
                  size="sm" variant="outline"
                  className="text-muted-foreground"
                  onClick={() => onToggleRisk("supheli_durum", "remove")}
                >
                  <X className="w-4 h-4 mr-1" /> Şüpheli Kaldır
                </Button>
              )}
            </div>

            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-1"><Tag className="w-4 h-4" /> Etiketler</h4>
              <div className="flex flex-wrap gap-1.5">
                {(app.risk_labels || []).length === 0 && (
                  <span className="text-xs text-muted-foreground">Etiket yok</span>
                )}
                {(app.risk_labels || []).map(label => {
                  const info = RISK_LABEL_MAP[label];
                  return (
                    <span key={label} className={`text-xs px-2.5 py-1 rounded-full ${info?.color || "bg-gray-100 text-gray-600"}`} data-testid={`risk-label-${label}`}>
                      {info?.label || label}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <DetailSection icon={User} title="İletişim">
                <DetailRow label="Ad Soyad" value={app.ad_soyad} />
                <DetailRow label="Yaş" value={app.yas} />
                <DetailRow label="Şehir/İlçe/Semt" value={`${app.sehir}, ${app.ilce}${app.semt ? ', ' + app.semt : ''}`} />
                <DetailRow label="İletişim Tercihi" value={app.iletisim_tercihi === "whatsapp" ? `WhatsApp: ${app.telefon}` : `Instagram: @${app.instagram_kullanici_adi}`} />
                <DetailRow label="Görüşme Tercihi" value={app.gorusme_tercihi === "yuz_yuze" ? "Yüz yüze" : "Online"} />
                {app.yurt_disindan_basvuru && <DetailRow label="Yurt Dışından" value="Evet" />}
                {app.referans_var && <DetailRow label="Referans" value={app.referans_bilgisi} />}
                <DetailRow label="Uygun Saatler" value={app.uygun_saat_araligi || "Belirtilmedi"} />
              </DetailSection>

              <DetailSection icon={Home} title="Yaşam Koşulları">
                <DetailRow label="Ev Tipi" value={app.ev_tipi} />
                <DetailRow label="Ev Durumu" value={app.ev_durumu === "kira" ? "Kirada" : "Kendi evi"} />
                {app.ev_durumu === "kira" && <DetailRow label="Ev Sahibi İzni" value={app.ev_sahibi_izni} />}
                <DetailRow label="Bina Yönetimi Kısıtı" value={app.bina_yonetimi_kisiti} />
              </DetailSection>

              <DetailSection icon={Users} title="Ev Halkı">
                <DetailRow label="Kişi Sayısı" value={app.evde_kisi_sayisi} />
                <DetailRow label="Herkes İstiyor mu?" value={app.evde_herkes_istiyor_mu} />
                <DetailRow label="Çocuk" value={app.cocuk_var_mi ? `Evet (${app.cocuk_yaslari || "yaş belirtilmedi"})` : "Yok"} />
                <DetailRow label="Alerji" value={app.alerji_var_mi} />
              </DetailSection>

              <DetailSection icon={PawPrint} title="Mevcut Hayvanlar">
                <DetailRow label="Kedi" value={app.kedi_var_mi ? "Var" : "Yok"} />
                <DetailRow label="Köpek" value={app.kopek_var_mi ? "Var" : "Yok"} />
                {(app.kedi_var_mi || app.kopek_var_mi) && (
                  <>
                    <DetailRow label="Aşı/Kısır" value={app.mevcut_hayvan_asi_kisir || "Belirtilmedi"} />
                    <DetailRow label="Tanıştırma Planı" value={app.tanistirma_plani || "Belirtilmedi"} />
                  </>
                )}
              </DetailSection>

              <DetailSection icon={Clock} title="Günlük Hayat">
                <DetailRow label="Yalnız Kalma" value={`${app.yalniz_kalma_saat} saat`} />
                <DetailRow label="Kim İlgilenecek" value={app.kim_ilgilenecek} />
                <DetailRow label="Tatilde" value={app.tatilde_kim_bakacak} />
                <DetailRow label="Yürüyüş" value={`Günde ${app.yuruyus_kac_kez} kez, ${app.yuruyus_toplam_sure}`} />
                <DetailRow label="Yaşam Alanı" value={app.kopek_nerede_yasayacak} />
              </DetailSection>

              <DetailSection icon={Heart} title="Deneyim ve Motivasyon">
                <DetailRow label="Önceki Deneyim" value={app.daha_once_kopek_bakti_mi ? "Evet" : "Hayır"} />
                <DetailRow label="Sahiplendirme Deneyimi" value={app.onceki_sahiplendirme_deneyimi || "Belirtilmedi"} />
                <DetailRow label="Motivasyon" value={app.neden_sahiplenmek_istiyor} />
                <DetailRow label="Zorluk Deneyimi" value={app.zorluk_deneyimi || "Belirtilmedi"} />
              </DetailSection>
            </div>

            {app.yas < 25 && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="text-sm font-medium text-orange-800 mb-2">25 Altı Özel Açıklamalar</h4>
                <DetailRow label="Neden değerlendirilmek istiyor" value={app.yas_25_alti_aciklama} />
                <DetailRow label="Sorumluluk planı" value={app.yas_25_alti_sorumluluk} />
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium mb-2">Notlar ({(app.notes || []).length})</h4>
              {(app.notes || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">Henüz not yok</p>
              ) : (
                <div className="space-y-2">
                  {(app.notes || []).map((note, i) => (
                    <div key={note.note_id || i} className="text-sm p-2 bg-muted/30 rounded-lg" data-testid={`note-${i}`}>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                        <Badge variant="outline" className="text-xs">{note.note_type}</Badge>
                        <span>{new Date(note.created_at).toLocaleString("tr-TR")}</span>
                      </div>
                      <p>{note.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 className="text-sm font-medium mb-2">Durum Geçmişi</h4>
              <div className="space-y-1">
                {(app.status_history || []).map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs" data-testid={`status-history-${i}`}>
                    <span className="text-muted-foreground w-36 shrink-0">
                      {new Date(entry.timestamp).toLocaleString("tr-TR")}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {STATUS_MAP[entry.status]?.label || entry.status}
                    </Badge>
                    {entry.note && <span className="text-muted-foreground">— {entry.note}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailSection({ icon: Icon, title, children }) {
  return (
    <div className="p-3 bg-muted/20 rounded-lg">
      <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
        <Icon className="w-4 h-4 text-primary" /> {title}
      </h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-muted-foreground shrink-0">{label}:</span>
      <span className="font-medium break-words">{value}</span>
    </div>
  );
}
